import React from "react";
import {useHistory} from "react-router-dom";
import {useSelector} from "react-redux";
import SimpleCard from "connectui/component/display/card/SimpleCard";

import Text from "connectui/component/display/Text";
import FlexContainer from "connectui/component/layout/FlexContainer";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import {useTheme} from "connectui/theme/ThemeProvider";
import {useTranslation} from "react-i18next";
import ButtonIconText from "../../../../connectui/component/input/button/ButtonIconText";
import EmptyButtonBase from "../../../../connectui/component/input/button/base/EmptyButtonBase";
import ChipRow from "../../../../connectui/component/display/chip/ChipRow";
import ChipIconText from "../../../../connectui/component/display/chip/ChipIconText";

const SummaryCard = ({cardKey, data, sx = {}, ...props}) => {
  const {theme} = useTheme();
  const {t} = useTranslation();
  const history = useHistory();
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );

  const handleMoreClick = (e) => {
    e.stopPropagation();
    if (data?.view_id) {
      history.push(`/admin/messages?view_id=${data.view_id}`);
    }
  };

  const handleChipClick = (customFilters) => {
    const currentViewId = data?.view_id;

    let existingFilters = [];
    if (currentViewId && user_profile) {
      const selectedView = user_profile.public_views?.find(
        (v) => v.id === currentViewId,
      );
      if (selectedView) {
        existingFilters = selectedView.queries || [];
      }
    }

    const mergedFilters = [...existingFilters, ...customFilters];

    const encodedFilters = encodeURIComponent(JSON.stringify(mergedFilters));
    history.push(`/admin/messages?custom_filters=${encodedFilters}`);
  };

  const createChip = (text, count, customFilters) => ({
    Component: ChipIconText,
    text: `${text} (${count})`,
    iconPosition: "end",
    icon: (
      <SvgIcon
        component={ICONS["arrow-up-right"]}
        sx={{
          width: "12px",
          height: "12px",
          color: theme.colors.gray700,
        }}
      />
    ),
    typographyVariant: "textXS",
    weight: "medium",
    textColor: `gray700`,
    onClick: () => handleChipClick(customFilters),
    sx: {
      backgroundColor: theme.colors.gray100,
      fontSize: theme.typography.textXS.fontSize,
      padding: theme.spacing(0.5, 1.5),
      cursor: "pointer",
      "&:hover": {
        backgroundColor: theme.colors.primary50,
      },
    },
  });

  const getCountChips = () => {
    const chips = [];

    if (data?.unread_messages && data.unread_messages > 0) {
      chips.push(
        createChip(
          t("dashboard.summary.unread-messages"),
          data.unread_messages,
          [
            {
              path: "unread_messages",
              value: true,
              operator: "==",
            },
          ],
        ),
      );
    }

    if (data?.pending_upsells && data.pending_upsells > 0) {
      chips.push(
        createChip(
          t("dashboard.summary.pending-upsells"),
          data.pending_upsells,
          [
            {
              path: "guest_booking_actions",
              value: "pending_upsell",
              operator: "==",
            },
          ],
        ),
      );
    }

    if (data?.pending_verifications && data.pending_verifications > 0) {
      chips.push(
        createChip(
          t("dashboard.summary.pending-verifications"),
          data.pending_verifications,
          [
            {
              path: "guest_booking_actions",
              value: "verify_guest",
              operator: "==",
            },
          ],
        ),
      );
    }

    return chips;
  };

  return (
    <SimpleCard
      customContent={
        <FlexContainer
          width="100%"
          height="100%"
          alignItems="flex-start"
          justifyContent="space-between"
          flexDirection="column"
          gap={theme.spacing(1)}
          sx={{position: "relative"}}
        >
          <FlexContainer
            sx={{width: "100%"}}
            alignItems="center"
            justifyContent="space-between"
          >
            <FlexContainer alignItems="center" gap={theme.spacing(1)}>
              <Text
                variant="textMD"
                weight="medium"
                color="textSecondary"
                sx={{textAlign: "center"}}
                skeleton={{enabled: !cardKey, width: "80px", height: "16px"}}
              >
                {cardKey ? t(`dashboard.summary.label.${cardKey}`) : ""}
              </Text>

              {cardKey === "automation_ratio" && (
                <SvgIcon
                  component={ICONS["ai"]}
                  sx={{
                    width: "20px",
                    height: "20px",
                    color: theme.colors.purple500,
                  }}
                />
              )}
            </FlexContainer>

            {data?.view_id && (
              <ButtonIconText
                buttonBase={EmptyButtonBase}
                label={t("dashboard.summary.more-button")}
                iconPosition={"end"}
                typographyVariant="textSM"
                onClick={handleMoreClick}
                color={theme.colors.primary}
                as="div"
                sx={{
                  padding: theme.spacing(0, 1.5),
                  color: theme.colors.primary,
                  "&:hover": {
                    backgroundColor: theme.colors.primary50,
                  },
                }}
                icon={
                  <SvgIcon
                    component={ICONS["arrow-up-right"]}
                    sx={{
                      width: "20px",
                      height: "20px",
                      marginTop: "2px",
                      marginLeft: theme.spacing(0.5),
                    }}
                  />
                }
              />
            )}
          </FlexContainer>

          <FlexContainer
            width="100%"
            alignItems="flex-end"
            justifyContent="space-between"
          >
            <Text
              variant="displayXS"
              weight="semibold"
              color="textPrimary"
              sx={{textAlign: "left"}}
              skeleton={{
                enabled: data === undefined,
                width: "60px",
                height: "24px",
              }}
            >
              {data !== undefined ? String(data.value ?? 0) : ""}
            </Text>

            {data && getCountChips().length > 0 && (
              <ChipRow
                chips={getCountChips()}
                gap={theme.spacing(1)}
                wrap={true}
                align="right"
              />
            )}
          </FlexContainer>
        </FlexContainer>
      }
      sx={{
        height: "100px",
        width: "100%",
        minWidth: 0,
        maxWidth: "none",
        flex: "1 1 auto",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.colors.white,
        boxSizing: "border-box",
        borderColor:
          cardKey === "automation_ratio"
            ? theme.colors.purple500
            : theme.colors.border,
        padding: theme.spacing(2, 3),
        ...sx,
      }}
      {...props}
    />
  );
};

export default SummaryCard;
